import { z } from 'zod'
import { clientIndustrySchema } from './clients.ts'
import { decimalSchema, timestampSchema, uuidSchema } from './common.ts'
import { customFieldValuesSchema } from './custom-fields.ts'
import { dealLineItemInputSchema } from './deal-line-items.ts'
import { dealStageKeySchema, dealStatusCategorySchema } from './deal-stages.ts'
import { commercialOperationTypeSchema } from './pricing.ts'
import { orderStageStatusSchema } from './production-stages.ts'
import { isoCurrencySchema } from './warehouse.ts'

/**
 * A stage is a key into `deal_stages`, not a member of a closed set.
 *
 * This was `z.enum([...])` until stages became admin-configurable. Everything that speaks stages
 * — saved views, filters, the board, this schema — still speaks the *key*, which is exactly why
 * renaming a stage in the admin screen leaves saved views working: the label moved, the key did
 * not. What is gone is compile-time exhaustiveness, so anything switching on a stage must now
 * look up its `role` (see `DealStageDefinition`) rather than compare to a literal.
 */
export const dealStageSchema = dealStageKeySchema
export type DealStage = string
export type DealBoardStage = string

export const DEAL_LOST_REASONS = [
  'price',
  'timeline',
  'competitor',
  'no_budget',
  'no_response',
  'other',
] as const

export const dealLostReasonSchema = z.enum(DEAL_LOST_REASONS)
export type DealLostReason = z.infer<typeof dealLostReasonSchema>

export const DEAL_LOST_REASON_LABELS: Record<DealLostReason, string> = {
  price: 'Ціна',
  timeline: 'Терміни',
  competitor: 'Конкурент',
  no_budget: 'Немає бюджету',
  no_response: 'Немає відповіді',
  other: 'Інше',
}

export const dealAmountSchema = z
  .string()
  .regex(/^\d+(\.\d{1,2})?$/, 'Вкажіть суму у форматі 1250 або 1250.50')

export const internalCommentSchema = z
  .string()
  .trim()
  .max(5000, 'Внутрішній коментар має містити не більше 5000 символів')
  .nullable()
  .transform((value) => value || null)

export const dealProductionSummarySchema = z.object({
  currentStageId: uuidSchema.nullable(),
  currentStageLabel: z.string().nullable(),
  currentStageStatus: orderStageStatusSchema.nullable(),
  /** An operator task completes through its task flow, not the deal's stage action. */
  currentStageIsTask: z.boolean(),
  completedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative(),
  progressBasis: z.enum(['stages', 'finished_quantity']),
  finishedQuantity: decimalSchema,
  productionTargetQuantity: decimalSchema.nullable(),
  productionTargetUnitSymbol: z.string().nullable(),
  progressPercent: z.number().int().min(0).max(100),
  isComplete: z.boolean(),
  isArchived: z.boolean(),
})

export type DealProductionSummary = z.infer<typeof dealProductionSummarySchema>

export const dealSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  clientId: uuidSchema,
  clientName: z.string(),
  clientEmail: z.string().nullable(),
  clientPhone: z.string().nullable(),
  stage: dealStageSchema,
  stageCategory: dealStatusCategorySchema,
  amount: dealAmountSchema,
  managerId: uuidSchema,
  managerName: z.string(),
  internalComment: z.string().nullable().optional(),
  nextAction: z.string().nullable(),
  nextActionAt: timestampSchema.nullable(),
  expectedCloseAt: timestampSchema.nullable(),
  aiScore: z.string().nullable(),
  position: decimalSchema,
  contacts: z.array(
    z.object({ id: uuidSchema, fullName: z.string(), role: z.string().nullable() }),
  ),
  customFields: customFieldValuesSchema,
  orderId: uuidSchema.nullable(),
  orderNumber: z.string().nullable(),
  orderArchivedAt: timestampSchema.nullable(),
  production: dealProductionSummarySchema.nullable().optional(),
  sellerEntityId: uuidSchema.nullable(),
  sellerEntityName: z.string().nullable(),
  currencyCode: isoCurrencySchema,
  vatTreatmentId: uuidSchema.nullable(),
  vatTreatmentName: z.string().nullable(),
  vatRate: decimalSchema.nullable(),
  pricesIncludeVat: z.boolean().nullable(),
  operationType: commercialOperationTypeSchema,
  counterpartyCountryCode: z.string().nullable(),
  exchangeRateSnapshotId: uuidSchema.nullable(),
  requestedRateDate: z.string().nullable(),
  effectiveRateDate: z.string().nullable(),
  exchangeRate: decimalSchema.nullable(),
  exchangeRateUnits: decimalSchema.nullable(),
  exchangeRateProvider: z.string().nullable(),
  uahEquivalent: decimalSchema,
  lostReason: dealLostReasonSchema.nullable(),
  lostNote: z.string().nullable(),
  closedAt: timestampSchema.nullable(),
  stageChangedAt: timestampSchema,
  createdBy: uuidSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
})

export type Deal = z.infer<typeof dealSchema>

export const dealManagerSchema = z.object({
  id: uuidSchema,
  name: z.string(),
})

export type DealManager = z.infer<typeof dealManagerSchema>

export const createDealInputSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  clientId: uuidSchema,
  sellerEntityId: uuidSchema.optional(),
  currencyCode: isoCurrencySchema.default('UAH'),
  vatTreatmentId: uuidSchema.nullable().default(null),
  operationType: commercialOperationTypeSchema.default('domestic'),
  counterpartyCountryCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/)
    .nullable()
    .default(null),
  contactIds: z.array(uuidSchema).default([]),
  amount: dealAmountSchema,
  expectedCloseAt: timestampSchema.nullable().optional(),
  internalComment: internalCommentSchema.optional(),
  lineItems: z.array(dealLineItemInputSchema).max(200).optional(),
  customFields: customFieldValuesSchema.default({}),
})

export type CreateDealInput = z.input<typeof createDealInputSchema>

export const dealFiltersSchema = z.strictObject({
  stage: z.array(dealStageSchema).optional(),
  category: z.array(dealStatusCategorySchema).optional(),
  managerId: z.array(uuidSchema).optional(),
  clientId: z.array(uuidSchema).optional(),
  industry: z.array(clientIndustrySchema).optional(),
  amountMin: dealAmountSchema.optional(),
  amountMax: dealAmountSchema.optional(),
  expectedCloseFrom: z.iso.date().optional(),
  expectedCloseTo: z.iso.date().optional(),
  hasOrder: z.boolean().optional(),
  showLost: z.boolean().optional(),
  search: z.string().trim().max(100).optional(),
})

export type DealFilters = z.infer<typeof dealFiltersSchema>

const queryArray = <T extends z.ZodType>(itemSchema: T) =>
  z.preprocess(
    (value) => (typeof value === 'string' ? value.split(',').filter(Boolean) : value),
    z.array(itemSchema),
  )

const queryBoolean = z.enum(['true', 'false']).transform((value) => value === 'true')

export const dealListQuerySchema = z
  .strictObject({
    stage: queryArray(dealStageSchema).optional(),
    category: queryArray(dealStatusCategorySchema).optional(),
    managerId: queryArray(uuidSchema).optional(),
    clientId: queryArray(uuidSchema).optional(),
    industry: queryArray(clientIndustrySchema).optional(),
    amountMin: dealAmountSchema.optional(),
    amountMax: dealAmountSchema.optional(),
    expectedCloseFrom: z.iso.date().optional(),
    expectedCloseTo: z.iso.date().optional(),
    hasOrder: queryBoolean.optional(),
    showLost: queryBoolean.optional(),
    search: z.string().trim().max(100).optional(),
    manager: uuidSchema.optional(),
  })
  .transform(
    ({ manager, managerId, ...query }): DealFilters => ({
      ...query,
      managerId: managerId ?? (manager ? [manager] : undefined),
    }),
  )

export type DealListQuery = z.infer<typeof dealListQuerySchema>

const lostTransitionFields = {
  lostReason: dealLostReasonSchema.optional(),
  lostNote: z.string().trim().max(1000).nullable().optional(),
}

export const updateDealStageInputSchema = z.object({
  stage: dealStageSchema,
  engineerId: z.string().trim().min(1).max(200).optional(),
  ...lostTransitionFields,
})

export type UpdateDealStageInput = z.infer<typeof updateDealStageInputSchema>

export const moveDealInputSchema = z.object({
  stage: dealStageSchema,
  position: decimalSchema,
  engineerId: z.string().trim().min(1).max(200).optional(),
  ...lostTransitionFields,
})

export type MoveDealInput = z.infer<typeof moveDealInputSchema>

export const updateDealManagerInputSchema = z.object({
  managerId: uuidSchema,
})

export type UpdateDealManagerInput = z.infer<typeof updateDealManagerInputSchema>

export const updateDealSellerInputSchema = z.object({
  sellerEntityId: uuidSchema,
})

export type UpdateDealSellerInput = z.infer<typeof updateDealSellerInputSchema>

export const updateDealDetailsInputSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  /**
   * Only writable while the deal has no line items. Once it does, `amount` is recomputed by
   * trigger and application code must not touch it (ADR 0003 D2) — the API rejects the attempt
   * rather than letting the two sources of truth drift.
   */
  amount: dealAmountSchema,
  internalComment: internalCommentSchema.optional(),
  expectedCloseAt: timestampSchema.nullable(),
  /**
   * The person this deal is being negotiated with, chosen from the client's existing contacts.
   * Contacts belong to the client, not to the deal — the same person carries across every deal
   * that client ever has, which is the whole reason they are a table rather than three columns.
   */
  /** Every person on the deal, replacing the previous list wholesale (ADR 0004 D3). */
  contactIds: z.array(uuidSchema),
  customFields: customFieldValuesSchema,
})

export type UpdateDealDetailsInput = z.infer<typeof updateDealDetailsInputSchema>

/** The order's path from sale to payment, as the order page draws it. */
export const DEAL_JOURNEY_STEP_KEYS = [
  'sale',
  'engineering',
  'reserve',
  'issue',
  'production',
  'shipping',
  'payment',
] as const

export const dealJourneyStepStateSchema = z.enum([
  'done',
  'current',
  'blocked',
  'upcoming',
  'skipped',
  'stopped',
])

/**
 * One step, already phrased: the server owns the facts and the access checks, so a role
 * without read rights on a module gets the state and a generic line, never its names or sums.
 */
export const dealJourneyStepSchema = z.object({
  key: z.enum(DEAL_JOURNEY_STEP_KEYS),
  title: z.string(),
  owner: z.string(),
  state: dealJourneyStepStateSchema,
  detail: z.string(),
})

export type DealJourneyStepKey = (typeof DEAL_JOURNEY_STEP_KEYS)[number]
export type DealJourneyStepState = z.infer<typeof dealJourneyStepStateSchema>
export type DealJourneyStep = z.infer<typeof dealJourneyStepSchema>
